import React, { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { Modal } from './Modal';
import { IconCopy, IconDownload, IconShare } from './Icons';
import { openWithLink, canNativeShare, copyImage, copyLink, downloadBlob, isLocalAddress, isMobileDevice, PICTURE_TARGETS, PictureTarget, shareImageFile, shareLink, TARGETS } from './share';
import { useToast } from './Toast';

interface Props {
  title: string;
  text: string; // message that goes with the link
  url: string;
  blob?: Blob | null; // when sharing a clipping, the picture itself can go too
  withLink?: boolean; // the link travels together with the picture
  views?: { kind: 'share' | 'clip'; token: string }; // show how many people opened this link
  onClose: () => void;
}

export function ShareTargets({ text, url, blob, withLink }: { text: string; url: string; blob?: Blob | null; withLink?: boolean }) {
  const toast = useToast();
  const msg = withLink ? (text ? text + '\n' + url : url) : text;
  const copy = async () => toast((await copyLink(url)) ? 'Link copied – it pastes as a clickable link' : 'Could not copy – select and copy the link', 'info');

  /** Phones: system share sheet with the picture attached. Computers: copy the picture so it can be pasted. */
  const sharePicture = async () => {
    if (!blob) return;
    if (await shareImageFile(blob, msg, msg)) return;
    if (await copyImage(blob)) toast('Picture copied – paste it (Ctrl+V) in your chat or mail', 'ok');
    else downloadBlob(blob, 'clipping.jpg');
  };

  const copyPicture = async () => {
    if (!blob) return;
    toast((await copyImage(blob)) ? 'Picture copied – paste it (Ctrl+V) where you want it' : 'This browser cannot copy pictures – use Download', 'info');
  };

  const sendTo = async (t: PictureTarget) => {
    if (!blob) return;
    if (isMobileDevice()) {
      // pick the app in the share sheet; the picture goes with it
      if (await shareImageFile(blob, msg, msg)) return;
    }
    const copying = copyImage(blob); // start now so the browser still counts this as a click
    window.open(withLink ? openWithLink(t, url) : t.open, /^(mailto|sms):/.test(t.open) ? '_self' : '_blank', 'noopener');
    toast((await copying) ? `Picture copied – press Ctrl+V in ${t.label} to send it` : 'Could not copy the picture – use Download and attach it', 'info');
  };

  const native = async () => {
    if (!(await shareLink(text, text, url))) toast('Sharing menu is not available here – use a button below.', 'info');
  };

  return (
    <div className="share">
      {blob && (
        <>
          <h3 className="share-h">Share the picture</h3>
          <div className="share-grid">
            <button className="chip strong" onClick={sharePicture}>
              <IconShare size={16} /> Share picture
            </button>
            <button className="chip" onClick={copyPicture}>
              <IconCopy size={16} /> Copy picture
            </button>
            <button className="chip" onClick={() => downloadBlob(blob, 'clipping.jpg')}>
              <IconDownload size={16} /> Download
            </button>
          </div>
          <div className="share-grid send-to">
            {PICTURE_TARGETS.map((t) => (
              <button key={t.id} className={'chip ' + t.id} onClick={() => sendTo(t)}>
                {t.label}
              </button>
            ))}
          </div>
          {!isMobileDevice() && <p className="muted small">On a computer the picture is copied and the app opens – just press Ctrl+V in the chat.</p>}
          <h3 className="share-h">Or share a link</h3>
        </>
      )}
      <div className="link-row">
        <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} aria-label="Link" />
        <button className="btn" onClick={copy}>
          <IconCopy size={16} /> Copy
        </button>
      </div>
      {isLocalAddress(url) && (
        <p className="share-note">
          This link starts with <strong>{new URL(url).host}</strong>, which only works on this computer or network. Chat apps will not make it clickable, and other people cannot open it. Open the site on its public https address (README: “Test it on the internet with https”) and share from there.
        </p>
      )}
      <p className="small">
        <a className="share-url" href={url} target="_blank" rel="noopener noreferrer">
          Open this link
        </a>
      </p>
      <div className="share-grid">
        {canNativeShare() && (
          <button className="chip" onClick={native}>
            <IconShare size={16} /> Share link…
          </button>
        )}
        {TARGETS.map((t) => (
          <a key={t.id} className={'chip ' + t.id} href={t.href(text, url)} target="_blank" rel="noopener noreferrer">
            {t.label}
          </a>
        ))}
      </div>
    </div>
  );
}

/** "Opened N times by M people" for a shared page link or clipping. Hidden while loading or when it cannot be read. */
export function ViewCount({ kind, token }: { kind: 'share' | 'clip'; token: string }) {
  const [v, setV] = useState<{ views: number; viewers: number } | null>(null);
  useEffect(() => {
    let live = true;
    (kind === 'share' ? api.shareViews(token) : api.clipViews(token)).then((r) => live && setV(r)).catch(() => undefined);
    return () => {
      live = false;
    };
  }, [kind, token]);
  if (!v) return null;
  return (
    <p className="view-count" title="Counted when someone opens the link. A refresh within 10 minutes is not counted again.">
      <span aria-hidden="true">👁</span> <strong>{v.viewers}</strong> {v.viewers === 1 ? 'viewer' : 'viewers'} · <strong>{v.views}</strong> {v.views === 1 ? 'view' : 'views'}
    </p>
  );
}

export function ShareDialog({ title, text, url, blob, withLink, views, onClose }: Props) {
  // show the picture that will be shared together with the link
  const preview = useMemo(() => (blob && withLink ? URL.createObjectURL(blob) : ''), [blob, withLink]);
  useEffect(() => () => (preview ? URL.revokeObjectURL(preview) : undefined), [preview]);
  return (
    <Modal title={title} onClose={onClose} sheet>
      {preview && (
        <div className="clip-preview">
          <img src={preview} alt="Page to be shared" />
        </div>
      )}
      {views && <ViewCount kind={views.kind} token={views.token} />}
      <ShareTargets text={text} url={url} blob={blob} withLink={withLink} />
    </Modal>
  );
}
